import get from 'lodash/get';

/**
 * Parse a node path into named coordinates.
 * Path layout: `group . sectionIndex . 'blocks' . slotId . blockIndex`.
 *
 *   'content.2'                → { group: 'content', sectionIndex: 2 }
 *   'content.2.blocks.main.1'  → { group, sectionIndex, slotId, blockIndex }
 */
export function parsePath(path) {
  const [group, sectionIndexStr, , slotId, blockIndexStr] =
    String(path).split('.');
  const sectionIndexNumber = Number(sectionIndexStr);
  const blockIndexNumber = Number(blockIndexStr);
  const sectionIndex = Number.isFinite(sectionIndexNumber)
    ? sectionIndexNumber
    : undefined;
  const blockIndex = Number.isFinite(blockIndexNumber)
    ? blockIndexNumber
    : undefined;

  return {
    group,
    sectionIndex,
    slotId,
    blockIndex,
    isBlock: blockIndex !== undefined,
  };
}

/**
 * Resolve where a new node will land when added to `parentPath`.
 * Returns `{ index, path }` — index within parent array, path of the new node.
 * If `index` is undefined, appends to the end.
 */
export function getInsertionTarget(data, parentPath, index) {
  const arr = get(data, parentPath);
  const resolvedIndex = index ?? arr?.length ?? 0;
  return { index: resolvedIndex, path: `${parentPath}.${resolvedIndex}` };
}

/**
 * Find the path of a section or block by its `id` across all regions.
 * The editor maps the iframe's `data-section-id` / `data-block-id` back to a
 * path; the iframe never deals with paths.
 */
export function findPathById(data, id) {
  if (!id) {
    return null;
  }
  for (const group of ['header', 'content', 'footer']) {
    const sections = data?.[group] ?? [];
    for (
      let sectionIndex = 0;
      sectionIndex < sections.length;
      sectionIndex += 1
    ) {
      const section = sections[sectionIndex];
      if (section?.id === id) {
        return `${group}.${sectionIndex}`;
      }
      const blocksBySlot = section?.blocks ?? {};
      for (const slotId of Object.keys(blocksBySlot)) {
        const blocks = blocksBySlot[slotId] ?? [];
        for (let blockIndex = 0; blockIndex < blocks.length; blockIndex += 1) {
          if (blocks[blockIndex]?.id === id) {
            return `${group}.${sectionIndex}.blocks.${slotId}.${blockIndex}`;
          }
        }
      }
    }
  }
  return null;
}

/** Read the node id at `path` (null if none). */
export function idAtPath(data, path) {
  if (!path) {
    return null;
  }
  return get(data, path)?.id ?? null;
}

/**
 * Resolve an `app:addItem` request (a node id + before/after) into the params
 * the add-item overlay needs: the parent path, insertion index, and — for
 * blocks — the slot and owning section type.
 */
export function resolveAddTarget(data, id, kind, position) {
  const path = findPathById(data, id);
  if (!path) {
    return null;
  }
  const parsed = parsePath(path);
  if (kind === 'section') {
    return {
      type: 'section',
      path: parsed.group,
      index:
        position === 'before' ? parsed.sectionIndex : parsed.sectionIndex + 1,
    };
  }
  const section = data?.[parsed.group]?.[parsed.sectionIndex];
  return {
    type: 'block',
    path: `${parsed.group}.${parsed.sectionIndex}.blocks.${parsed.slotId}`,
    slotId: parsed.slotId,
    sectionType: section?.type,
    index: position === 'before' ? parsed.blockIndex : parsed.blockIndex + 1,
  };
}
